import React, { useState } from 'react';
import { X, Download, Eye, FileText, Image, File } from 'lucide-react';
import { ref, getBlob } from 'firebase/storage';
import { storage } from '../../firebase';
import { formatFileSize } from '../../services/storageService';
import './FilesModal.css';

const FilesModal = ({ title, files, onClose }) => {
  const handleDownload = async (url, fileName, storagePath) => {
    try {
      if (storagePath) {
        const fileRef = ref(storage, storagePath);
        const blob = await getBlob(fileRef);
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } else {
        const response = await fetch(url);
        const blob = await response.blob();
        const blobUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      }
    } catch (error) {
      window.open(url, '_blank');
    }
  };

  const [previewFile, setPreviewFile] = useState(null);

  const handleView = (file) => {
    if (file.fileType?.startsWith('image/')) {
      setPreviewFile(file);
    } else {
      window.open(file.downloadURL, '_blank');
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType?.startsWith('image/')) return <Image size={16} />;
    if (fileType?.includes('pdf')) return <FileText size={16} />;
    return <File size={16} />;
  };

  const isViewable = (fileType) =>
    fileType?.startsWith('image/') || fileType?.includes('pdf');

  const getCategoryLabel = (category) => {
    const labels = { draft: 'Draft', final: 'Final', client: 'Mine', general: 'General' };
    return labels[category] || category;
  };

  const getCategoryClass = (category) => `fm-category-badge fm-category-${category}`;

  return (
    <div className="fm-overlay" onClick={onClose}>
      <div className="fm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="fm-header">
          <h3>{title}</h3>
          <button className="fm-close-btn" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="fm-body">
          {files.length === 0 ? (
            <div className="fm-empty">
              <FileText size={32} />
              <p>No files found</p>
            </div>
          ) : (
            <table className="fm-table">
              <thead>
                <tr>
                  <th>File</th>
                  <th>Category</th>
                  <th>Size</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {files.map(file => (
                  <tr key={file.id}>
                    <td className="fm-file-name-cell">
                      {getFileIcon(file.fileType)}
                      <span>{file.fileName}</span>
                    </td>
                    <td>
                      <span className={getCategoryClass(file.category)}>
                        {getCategoryLabel(file.category)}
                      </span>
                    </td>
                    <td>{formatFileSize(file.fileSize)}</td>
                    <td>
                      {file.uploadedAt?.toDate().toLocaleDateString('en-GB', {
                        day: '2-digit', month: 'short', year: 'numeric'
                      })}
                    </td>
                    <td className="fm-actions-cell">
                      {isViewable(file.fileType) && (
                        <button
                          onClick={() => handleView(file)}
                          className="fm-btn fm-btn-view"
                          title="View"
                        >
                          <Eye size={15} />
                        </button>
                      )}
                      <button
                        onClick={() => handleDownload(file.downloadURL, file.fileName, file.storagePath)}
                        className="fm-btn fm-btn-download"
                        title="Download"
                      >
                        <Download size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {previewFile && (
        <div className="fm-preview-overlay" onClick={() => setPreviewFile(null)}>
          <div className="fm-preview-modal" onClick={e => e.stopPropagation()}>
            <div className="fm-preview-header">
              <span>{previewFile.fileName}</span>
              <div style={{display:'flex',gap:8}}>
                <button className="fm-btn fm-btn-download" onClick={() => handleDownload(previewFile.downloadURL, previewFile.fileName, previewFile.storagePath)}>
                  <Download size={16} />
                </button>
                <button className="fm-close-btn" onClick={() => setPreviewFile(null)}><X size={20} /></button>
              </div>
            </div>
            <div className="fm-preview-body">
              <img src={previewFile.downloadURL} alt={previewFile.fileName} className="fm-preview-image" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FilesModal;
